import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BaseService } from '../../base.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Invoice } from '../../models/invoice';
import { Paginated } from '../../models/paginated';
import { InvoiceFilter } from '../../models/invoice-filter';
import {WeekInvoices} from '../invoice-week-selector/invoice-week-table/invoice-week-table.component';

@Injectable()

/**
 * InvoicesService contains logic for making api requests for invoices
 */
export class InvoicesService extends BaseService
{
	private api: string;

	/**
	 * InvoicesService constructor
	 *
	 * @param {HttpService} http
	 */
	constructor(private http: HttpClient)
	{
		super();
		this.api = environment.api_url;
	}

	/**
	 * Makes a get request to retrieve a collection of invoices
	 *
	 * @param {InvoiceFilter} filters
	 *
	 * @returns {Observable<Invoice[]>}
	 */
	public getInvoices(filters: InvoiceFilter): Observable<Invoice[]>
	{
		const parameters = this.getFilterParameters(filters).join('&');
		return this.http.get(this.api + 'invoices?' + parameters)
			.map(invoices => invoices['data'] as Invoice[]);
	}

	/**
	 * Makes a get request to get a paginated collection of invoices
	 *
	 * @param {number} page
	 * @param {InvoiceFilter} filters
	 *
	 * @returns {Observable<Paginated<Invoice[]>>}
	 */
	public getPaginatedInvoices(page: number, filters: InvoiceFilter): Observable<Paginated<Invoice[]>>
	{
		const parameters = this.getFilterParameters(filters);
		parameters.push('page=' + page);

		return this.http.get(this.api + 'invoices?' + parameters.join('&'))
			.map(invoices => invoices as Paginated<Invoice[]>);
	}

  /**
   * Makes a get request to retrieve the counts for the invoices of the given year
   *
   * @param year
   *
   * @return {Observable<WeekInvoices[]>>}
   */
	public getInvoicesCounts(year: number): Observable<WeekInvoices[]>
  {
    return this.http.get(this.api + 'invoices/counts/' + year)
      .map(invoicesCount => invoicesCount as WeekInvoices[]);
  }

	/**
	* Makes a post request to store an invoice
	*
	* @param {Invoice} invoice
	*
	* @returns {Observable<Invoice>}
	*/
	public storeInvoice(invoice: Invoice): Observable<Invoice>
	{
		console.log(this.api + 'invoice', invoice);
		return this.http.post(this.api + 'invoices', invoice)
			.map(response => response['data'] as Invoice);
	}


	/**
	* Makes a get request get retrieve an invoice by id
	*
	* @param {number} id
	*
	* @returns {Observable<Invoice>}
	*/
	public getInvoice(id: number): Observable<Invoice>
	{
		return this.http.get(this.api + 'invoices/' + id)
			.map(invoice => invoice['data'] as Invoice);
	}


	public getAccountancyExport(params): Promise<string>
	{
		return this.http.get(this.api + 'invoices/export/accountancy?' + params.join('&') )
			.toPromise()
			.catch(this.handleError);
	}

	/**
	 * Makes a get request get retrieve an invoice ubl xml by id
	 *
	 * @param {number} id
	 *
	 * @returns {Promise<string>}
	 */
	public getInvoiceUBL(params): Promise<string>
	{
		return this.http.get(this.api + 'invoices/export/ubl?' + params.join('&') )
			.toPromise()
			.catch(this.handleError);
	}

	/**
	 * Makes a post request to set the current organisations' invoice settings
	 *
	 * @param settings
	 *
	 * @returns {Observable<Response>}
	 */
	public setInvoiceSettings(settings): Observable<Response>
	{
		return this.http.post(this.api + 'invoices/settings', settings)
			.map(response => response as Response);
	}

	/**
	 * Makes a get request to send the selected emails to their customer
	 *
	 * @param {Array<string>} params
	 *
	 * @returns {Observable<any>}
	 */
	public sendInvoices(params: Array<string>)
	{
		return this.http.get(this.api + 'invoices/send?' + params.join('&'));
	}

	/**
	 * Returns an array of request parameters for a given invoice filter
	 *
	 * @param {InvoiceFilter} filters
	 *
	 * @returns {Array<string>}
	 */
	private getFilterParameters(filters: InvoiceFilter): Array<string>
	{
		const parameters: Array<string> = [];

		if (filters['year']) {
			parameters.push('year=' + filters['year']);
		}
		if (filters['week']) {
			parameters.push('week=' + filters['week']);
		}
		if (filters['search']) {
			parameters.push('search=' + filters['search']);
		}

		return parameters;
	}
}



// WEBPACK FOOTER //
// ./src/app/invoices/services/invoices.service.ts